import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import {
  createViewEventElement,
  CartErrorEvent,
  CartDiscountUpdateEvent,
  CartLinesUpdateEvent,
  CartNoteUpdateEvent,
  StandardEvents,
} from '@shopify/events';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
export class CartItemsComponent extends createViewEventElement(Component) {
  #debouncedOnChange = debounce(
    /** @param {Event} event */
    (event) => {
      if (event instanceof QuantitySelectorUpdateEvent) this.#onQuantityChange(event);
    },
    300
  );
  /** @type {Promise<any> | null} */
  #pendingCartFetch = null;

  /**
   * True when the event was dispatched from outside this cart-items-component (e.g.
   * `Shopify.actions.updateCart(...)` from an external app, or the SFAPI default
   * handler). Internal dispatchers (cart-discount-component, cart-note) live inside
   * `this` and either morph the section themselves or don't need a refresh — running
   * a fallback render in that case double-renders and can clobber form state.
   * @param {Event} event
   */
  #isExternalCartUpdate(event) {
    return !(event.target instanceof Node) || !this.contains(event.target);
  }

  /** @param {CartDiscountUpdateEvent} event */
  #handleDiscountUpdate = (event) => {
    const external = this.#isExternalCartUpdate(event);
    event.promise
      ?.then(({ detail }) => {
        const sectionsHtml = detail?.sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
          this.#updateCartQuantitySelectorButtonStates();
        } else if (external) {
          // External caller (Shopify.actions.updateCart or SFAPI default handler) didn't
          // attach sections; refetch so the discount UI reflects the post-mutation cart.
          // Internal cart-discount-component morphs the section itself — no fallback needed.
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /** @param {CartNoteUpdateEvent} event */
  #handleNoteUpdate = (event) => {
    // Internal cart-note dispatches don't need a section refresh — the user typed the
    // value and the textarea retains it. Only external callers need the UI synced.
    if (!this.#isExternalCartUpdate(event)) return;
    event.promise
      ?.then(({ detail }) => {
        const sections = /** @type {Record<string, string> | undefined} */ (detail?.sections);
        const sectionsHtml = sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
        } else {
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.addEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.removeEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.removeEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /*
   * ══════════════════════════════════════════════════════════════════
   * PARCHE DE LA MARCA · EL FOCO NO SE CAE AL <body> AL QUITAR · 10/09/2026
   * ══════════════════════════════════════════════════════════════════
   * `onLineItemRemove` quita del DOM la fila entera, y dentro de esa fila
   * está el botón que acaba de pulsarse. Cuando el navegador se queda sin
   * el elemento enfocado devuelve el foco al `<body>`, así que quien navega
   * con el teclado vuelve a empezar por la cabecera de la tienda y no se
   * entera de qué ha pasado con la línea que quería quitar.
   *
   * Medido el 10/09/2026 contra `shopify theme dev`, con este parche
   * neutralizado —o sea, el estado en el que estaba el tema—, en el cajón
   * con el ratón y en `/cart` con Enter:
   *
   *   foco antes:   BUTTON.ml-ces__quitar
   *   foco después: BODY
   *
   * Y con el parche puesto, cinco de cinco por caso, en las dos superficies
   * y con los dos gestos: `BUTTON.ml-ces__quitar` cuando queda alguna línea
   * y `H2.ml-ces__vacio-t` cuando se quita la última, las dos veces sobre un
   * elemento que de verdad está en pantalla.
   *
   * UNA COSA QUE NO ES DE ESTE PARCHE Y CONVIENE SABER: en un teléfono de
   * 390 px, pulsar «Quitar» con **Enter** hace que Horizon cierre el cajón
   * y devuelva el foco al botón de la cabecera. Con el ratón no pasa. Se
   * comprobó con este parche neutralizado y ocurre igual, así que es suyo y
   * no nuestro; ahí el foco tampoco acaba en el `<body>`, pero acaba fuera
   * de la cesta.
   *
   * Es la misma regla que este proyecto ya tiene escrita para las dos
   * barras de compra —si escondes el elemento que tiene el foco, muévelo
   * antes— y que allí vigila N30. Aquí faltaba, y lo llamativo es que el
   * DESTINO ya estaba puesto: el titular del estado vacío de
   * `snippets/ml-carrito-cuerpo.liquid` lleva `tabindex="-1"` desde el
   * principio, con un comentario al lado que dice que es adonde va el foco
   * al quitar la última línea, y `assets/ml-carrito.css` le dibuja su
   * `:focus-visible`. Nadie lo enfocaba nunca. Un gancho presente y sin
   * conectar pasa cualquier prueba de marcado, que es la lección que
   * `dev/navegador/LEEME.md` cuenta con los cuatro fallos del carrito.
   *
   * SE RECORREN LAS DOS DIRECCIONES, que es la otra mitad de la regla:
   *   · al quitar, el foco se mueve ANTES de que la fila desaparezca;
   *   · y cuando el HTML vuelve —`morphSection` reconstruye la sección con
   *     lo que contesta el servidor, y en el cajón la cesta puede pasar de
   *     vacía a llena otra vez— se vuelve a colocar, pero SOLO si lo había
   *     puesto este parche y el navegador lo ha soltado. Sin eso el
   *     arreglo dura los milisegundos que tarda la respuesta.
   *
   * ARCHIVO DE HORIZON. Al fusionar una versión nueva hay que volver a
   * ponerlo, igual que los parches de `assets/utilities.js` y
   * `assets/view-transitions.js`. Lo vigila la prueba Q68.
   */

  /** Verdadero mientras el foco de la página lo haya puesto este parche. */
  #mlFocoNuestro = false;

  /** Número de línea que se quitó, para saber qué sitio ocupar al volver. */
  #mlLineaQuitada = 0;

  /**
   * Los botones de quitar que hay ahora mismo en el DOM, en orden.
   *
   * Se preguntan por el enlace declarativo de Horizon y no por la clase de
   * la marca a propósito: `on:click="/onLineItemRemove/…"` es el mismo
   * contrato que hace que el botón funcione, así que un cambio ahí rompe
   * las dos cosas a la vez y no deja este parche apuntando al vacío en
   * silencio. Y se consulta el DOM en vez de `this.refs`, porque los refs
   * los recalcula un MutationObserver y después de un morph todavía no
   * están al día.
   *
   * @returns {HTMLElement[]}
   */
  #mlBotonesQuitar() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[on\\:click^="/onLineItemRemove/"]')].filter(
      (nodo) => nodo instanceof HTMLElement && !nodo.hasAttribute('hidden') && !nodo.hasAttribute('disabled')
    ));
  }

  /**
   * Coloca el foco en el mejor sitio que quede dentro de la cesta.
   *
   * El orden es el que espera quien está quitando líneas: el botón de la
   * línea que ocupa AHORA el sitio de la que se fue, el de la última si se
   * quitó la de abajo, y el titular del estado vacío si no queda ninguna.
   * Ese titular es el único destino que no es un control, y por eso lleva
   * `tabindex="-1"` escrito en el Liquid.
   *
   * @param {Element[]} [seVan] - Filas que están a punto de desaparecer.
   * @returns {boolean} - Si el foco ha acabado donde se pedía.
   */
  #mlColocarFoco(seVan = []) {
    const botones = this.#mlBotonesQuitar().filter((b) => !seVan.some((fila) => fila.contains(b)));
    /** @type {HTMLElement | null} */
    let destino = null;

    if (botones.length) {
      const sitio = Math.min(Math.max(this.#mlLineaQuitada, 1), botones.length);
      destino = botones[sitio - 1] ?? botones[botones.length - 1];
    } else {
      const titulo = this.querySelector('.ml-ces__vacio-t') ?? this.querySelector('[tabindex="-1"]');
      if (titulo instanceof HTMLElement) destino = titulo;
    }

    if (!destino) return false;
    destino.focus();
    const puesto = document.activeElement === destino;
    if (puesto) this.#mlFocoNuestro = true;
    return puesto;
  }

  /**
   * Devuelve el foco después de un morph, si lo teníamos y se ha caído.
   *
   * No se toca nunca el foco de quien no lo tenía dentro de esta cesta: si
   * el visitante se ha ido a otro sitio de la página mientras el servidor
   * contestaba, mandarlo de vuelta al carrito sería peor que perderlo.
   */
  #mlDevolverFoco() {
    if (!this.#mlFocoNuestro) return;
    const activo = document.activeElement;
    if (activo instanceof HTMLElement) {
      if (activo !== document.body) {
        if (this.contains(activo)) return;
        this.#mlFocoNuestro = false;
        return;
      }
    }
    this.#mlColocarFoco();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    /* El foco se mueve ANTES de esconder nada, y solo si de verdad estaba
       dentro de lo que se va: ver la cabecera del parche, arriba. */
    const teniaElFoco =
      document.activeElement instanceof HTMLElement &&
      document.activeElement !== document.body &&
      rowsToRemove.some((row) => row.contains(/** @type {Node} */ (document.activeElement)));
    if (teniaElFoco) {
      this.#mlLineaQuitada = line;
      if (!isEmptyCart) this.#mlColocarFoco(rowsToRemove);
    }

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        document.getElementById('cart-drawer-heading')?.remove();
        this.replaceChildren(clone);
        /* Con la cesta vacía el destino no existe hasta que el estado vacío
           está puesto, así que aquí no se puede mover el foco «antes»: se
           mueve en cuanto hay adónde, dentro del mismo cambio de DOM. */
        if (teniaElFoco) this.#mlColocarFoco();
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();
    const lineId = this.refs.cartItemRows[line - 1]?.dataset.key ?? '';
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: config.action === 'change' && quantity > 0 ? 'update' : 'remove',
        context: 'cart',
        lines: [{ id: lineId, quantity }],
        promise: deferredUpdatePromise.promise,
      })
    );

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          deferredUpdatePromise.reject(new Error(parsedResponseText.errors));
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        deferredUpdatePromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(parsedResponseText),
          detail: {
            sections: parsedResponseText.sections,
            items: parsedResponseText.items,
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            didError: false,
          },
        });

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], {
          mode: this.isDrawer ? 'hydration' : 'full',
        });

        this.#updateCartQuantitySelectorButtonStates();

        /* La otra dirección del parche del foco: el morph reconstruye la
           sección con lo que ha contestado el servidor y puede llevarse por
           delante el nodo donde acabábamos de dejarlo. Ver la cabecera. */
        this.#mlDevolverFoco();
      })
      .catch((error) => {
        console.error(error);
        deferredUpdatePromise.reject(error);

        this.dispatchEvent(
          new CartErrorEvent({
            error: error?.message || 'Failed to update cart',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');

    this.dispatchEvent(
      new CartErrorEvent({
        error: parsedResponseText.errors || 'Cart update failed',
        code: 'INVALID',
      })
    );
  };

  /**
   * Handles the cart update.
   *
   * @param {CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event.target === this) return;

    event.promise
      ?.then(async ({ detail }) => {
        const sections = detail?.sections;
        const cartItemsHtml = sections?.[this.sectionId];
        // Animate empty → non-empty in the drawer (possible in squeeze mode
        // where the page is interactive alongside the open drawer). This also
        // needs the response stylesheet because it adds the cart summary markup.
        const wasEmptyCartDrawer = this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null;
        /** @type {'hydration' | 'full'} */
        const mode = this.isDrawer ? 'hydration' : 'full';
        const morphOptions = {
          mode,
          injectStylesheet: wasEmptyCartDrawer,
        };

        if (cartItemsHtml) {
          const existingKeys = new Set(this.refs.cartItemRows?.map((row) => row.dataset.key) ?? []);

          if (wasEmptyCartDrawer) {
            startViewTransition(() => {
              morphSection(this.sectionId, cartItemsHtml, morphOptions);
            }, ['fill-cart-drawer']);
          } else {
            await morphSection(this.sectionId, cartItemsHtml, morphOptions);
          }

          // Animate newly added rows (reverse of the remove animation).
          if (!wasEmptyCartDrawer && !prefersReducedMotion()) {
            for (const row of this.refs.cartItemRows ?? []) {
              if (!existingKeys.has(row.dataset.key)) {
                row.classList.add('adding');
                onAnimationEnd(row, () => row.classList.remove('adding'));
              }
            }
          }

          // Update button states for all cart quantity selectors after morph
          this.#updateCartQuantitySelectorButtonStates();

          /* Y el camino de vuelta del cajón: con la cesta vacía el foco se
             quedó en el titular del estado vacío, y si desde la página de
             detrás se añade algo ese titular desaparece. Ver la cabecera. */
          this.#mlDevolverFoco();
        } else {
          sectionRenderer.renderSection(this.sectionId, { cache: false, ...morphOptions });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  async fetchCartData() {
    if (this.#pendingCartFetch) return this.#pendingCartFetch;

    this.#pendingCartFetch = (async () => {
      const response = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status} ${response.statusText}`);
      const data = await response.json();
      return data;
    })().finally(() => {
      this.#pendingCartFetch = null;
    });

    return this.#pendingCartFetch;
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
